import {Translatable} from 'common/i18n';
import {assert, capitalize, getOwnAttr} from 'common/util';
import {MPProperties, PATH_SEPARATOR} from 'common/constants';
import type {TooltipAttrs} from 'common/components/tooltip/types';
import {Behavior, Segmentation} from 'common/types/reports/query';
import {COMPUTED_LIST_PROPERTIES_LABELS} from 'common/query-builder/query-block/properties-menu/constants';
import {SvgIconType} from 'common/components/svg-icon/types';
import {LocalizationStore} from 'common/i18n/store';
import {GroupClause} from 'common/types/reports/bookmark';

import {getPropertyDisplayName, getPropertyDisplayNameOld} from '../display-name-registry';
import {
  EXPERIMENT_PROPERTY_PREFIX,
  FullPropertyType,
  MappedPropertyValues,
  PropertyType,
  ResourceType,
  SCD_PROFILE_PROPERTY_PREFIX,
} from '../constants';
import {eventPropertyDefinitionsStore} from '../stores/property-definitions';
import {customPropertiesStore} from '../stores/custom-properties';
import {DURATION_PROPERTY_NAME} from '../insights/util/constants';
import {renameEvent} from './events';
import {getPropertyValueMappings} from './mappings';

type MetaOptions = {isCustom?: boolean; isScd?: boolean};

export const propertiesUtils = {
  renameProperty,
};

export const objectAccessSymbol = PATH_SEPARATOR;

export enum ConstantPropertyKeys {
  EmptyString = `(empty string)`,
  EmptyList = `(empty list)`,
  NotSet = `(not set)`,
  NotApplicable = `(not applicable)`,
  NotANumber = `(not a number)`,
}

export enum ConstantPropertyValues {
  EmptyString = ``,
  EmptyList = `$empty_list`,
  NotSet = `undefined`,
  NotApplicable = `$not_applicable`,
  NotANumber = `$non_numeric_values`,
  RemainingValues = `$remaining_values`,
}

const enum SpecialPropertyKeys {
  TimeToConvert = `$time_to_conversion`,
  RecurringRevenueChange = `$revenue_change`,
}

const enum SpecialPropertyValues {
  TimeToConvert = `Time to Convert`,
  RecurringRevenueChange = `Recurring Revenue Change`,
}

const SpecialPropertyMapping = {
  [SpecialPropertyKeys.TimeToConvert]: SpecialPropertyValues.TimeToConvert,
  [SpecialPropertyKeys.RecurringRevenueChange]: SpecialPropertyValues.RecurringRevenueChange,
};

export const ConstantPropertyMapper = {
  [ConstantPropertyKeys.EmptyString]: ConstantPropertyValues.EmptyString,
  [ConstantPropertyKeys.EmptyList]: ConstantPropertyValues.EmptyList,
  [ConstantPropertyKeys.NotSet]: ConstantPropertyValues.NotSet,
  [ConstantPropertyKeys.NotApplicable]: ConstantPropertyValues.NotApplicable,
  [ConstantPropertyKeys.NotANumber]: ConstantPropertyValues.NotANumber,
};

export const ConstantPropertyValueSet = new Set<ConstantPropertyValues>(Object.values(ConstantPropertyValues));

export type ConstantPropertyValueInfo = {label: string; tooltip?: TooltipAttrs};
export const ConstantPropertyValuesInfo: Record<ConstantPropertyValues, ConstantPropertyValueInfo> = {
  [ConstantPropertyValues.EmptyList]: {
    label: `(empty list)`,
    tooltip: {
      text: `This property is set to an empty list.\n[Learn More](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#list-property-support)`,
    },
  },
  [ConstantPropertyValues.NotSet]: {
    label: `(not set)`,
    tooltip: {
      text: `This property does not contain any value.\n[Learn More](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#undefined-and-null-properties)`,
    },
  },
  [ConstantPropertyValues.EmptyString]: {
    label: `(empty string)`,
    tooltip: {text: `This property is set to an empty string value`},
  },
  [ConstantPropertyValues.NotApplicable]: {
    label: `(not applicable)`,
  },
  [ConstantPropertyValues.RemainingValues]: {
    label: `(remaining values)`,
    tooltip: {text: `Includes all defined values that are ungrouped.`},
  },
  [ConstantPropertyValues.NotANumber]: {
    label: `(non-numeric values)`,
    tooltip: {text: `The data in this group are in a format that can not be read as numbers (e.g. string)`},
  },
};

export function getTooltipFromLabel(label: string) {
  const defaultProp = Object.values(ConstantPropertyValuesInfo).find((info) => info.label === label);
  return defaultProp ? defaultProp.tooltip?.text : null;
}

export function getIconForPropertyType(
  type: PropertyType | FullPropertyType,
  {isCustom, isScd}: MetaOptions = {isCustom: false, isScd: false},
): SvgIconType | null {
  const iconNameBuffer = [`type`, isCustom && `custom`, isScd && `historic`].filter(Boolean);

  let simpleType: PropertyType;

  if (type && typeof type === `object`) {
    simpleType = type.isList ? PropertyType.List : type.type;
  } else {
    simpleType = type as PropertyType;
  }

  switch (simpleType) {
    case PropertyType.Dimension:
      return `table`;
    case PropertyType.String:
      iconNameBuffer.push(`text`);
      break;
    case PropertyType.Datetime:
      iconNameBuffer.push(`date`);
      break;
    case PropertyType.Boolean:
    case PropertyType.List:
    case PropertyType.Number:
    case PropertyType.Object:
      iconNameBuffer.push(simpleType);
      break;
    default:
      return null;
  }
  return iconNameBuffer.join(`-`) as SvgIconType;
}

const PROPERTY_TYPE_LABEL_MAP: Record<PropertyType, Translatable> = {
  boolean: ({t}) => t(`query-builder.data-type.boolean`),
  datetime: ({t}) => t(`query-builder.data-type.date`),
  dimension: ``,
  list: ({t}) => t(`query-builder.data-type.list`),
  number: ({t}) => t(`query-builder.data-type.number`),
  object: ({t}) => t(`query-builder.data-type.object`),
  other: ``,
  string: ({t}) => t(`query-builder.data-type.string`),
  unknown: ``,
};

export function getLabelForPropertyType(type: PropertyType, {isCustom}: MetaOptions = {isCustom: false}): Translatable {
  const label = PROPERTY_TYPE_LABEL_MAP[type];
  return label && isCustom ? ({t, print}) => t(`query-builder.data-type.custom-type`, {type: print(label)}) : label;
}

/** For object properties */
function getPropertyObjectKeySuffix(
  propertyObjectKey: string | string[] | null | undefined,
  _i18n: LocalizationStore,
  {isExperiments} = {isExperiments: false},
) {
  if (!propertyObjectKey) {
    return ``;
  }

  if (isExperiments) {
    // special case for $experiments
    const experimentId = Array.isArray(propertyObjectKey) ? propertyObjectKey[0] : propertyObjectKey;
    return `${objectAccessSymbol}${renamePropertyValue(experimentId, MappedPropertyValues.ExperimentId)}`;
  }

  return `${objectAccessSymbol}${
    Array.isArray(propertyObjectKey) ? propertyObjectKey.join(objectAccessSymbol) : propertyObjectKey
  }`;
}

/** For list of object properties */
function getListItemGroupLabel(listItemGroup: Segmentation, propertyName: string, _i18n: LocalizationStore) {
  assert(
    Array.isArray(listItemGroup.propertyName) === false,
    `multiple property names not supported for list of objects`,
  );

  const listPropertyName = listItemGroup.propertyName;

  if (COMPUTED_LIST_PROPERTIES_LABELS[listPropertyName]) {
    propertyName = `${COMPUTED_LIST_PROPERTIES_LABELS[listPropertyName]} of ${propertyName}`;
  } else {
    propertyName = propertyName + PATH_SEPARATOR + listPropertyName;
  }

  if (Array.isArray(listItemGroup.propertyObjectKey)) {
    propertyName = [propertyName, ...listItemGroup.propertyObjectKey].join(PATH_SEPARATOR);
  }

  return propertyName;
}

export function isExperimentPropertyName(propertyName: string | string[] | undefined | null) {
  if (Array.isArray(propertyName) || !propertyName) return false;
  return propertyName?.startsWith(EXPERIMENT_PROPERTY_PREFIX);
}

export function isScdPropertyName(
  propertyName: Record<any, any> | boolean | number | string | string[] | undefined | null,
) {
  if (typeof propertyName === `object` || typeof propertyName === `number` || typeof propertyName === `boolean`) {
    return false;
  }
  if (Array.isArray(propertyName) || !propertyName) {
    return false;
  }
  return propertyName?.startsWith(SCD_PROFILE_PROPERTY_PREFIX);
}

export function isAggregationUserFreqByGroupBehavior(behavior: Behavior) {
  return behavior?.behaviorType === `$behavior_aggregation`;
}

const INVALID_SCD_PROPERTY_TYPE = [PropertyType.List, PropertyType.Object];
export function isInvalidScdPropertyType(propertyType: PropertyType) {
  return INVALID_SCD_PROPERTY_TYPE.includes(propertyType);
}

/**
 * SCD props are persisted in lexicon with the `$scd:` prefix due to a constraint
 * of how we prevent properties to have duplicates in a project.
 *
 * For SCD props we wish to allow duplicates for properties being defined in engage and
 * being ingested via an SCD event.
 */
export function renameSCDProp(propertyName: string) {
  if (isScdPropertyName(propertyName)) {
    return propertyName.replace(SCD_PROFILE_PROPERTY_PREFIX, ``);
  }

  return propertyName;
}

function _renameProperty(
  propertyName: string | string[],
  resourceType: ResourceType,
  i18n: LocalizationStore,
  {
    dataGroupId = null,
    customProperty = null,
    customPropertyId = null,
    behavior = null,
  }: {
    dataGroupId?: any;
    customProperty?: any;
    customPropertyId?: any;
    propertyObjectKey?: any;
    listItemGroup?: any;
    behavior?: any;
  } = {},
): string {
  if (customPropertyId) {
    const storeProperty = customPropertiesStore.value[customPropertyId];
    if (storeProperty) {
      customProperty = storeProperty;
    }
  }
  if (customProperty && customProperty.name) {
    if (Array.isArray(propertyName)) {
      return [customProperty.name, _renameProperty(propertyName[1], resourceType, i18n, {dataGroupId})].join(`.`);
    }
    return customProperty.name;
  } else if (Array.isArray(propertyName)) {
    return [
      _renameProperty(propertyName[0], resourceType, i18n),
      _renameProperty(propertyName[1], resourceType, i18n, {dataGroupId, customProperty}),
    ].join(objectAccessSymbol);
  } else if (behavior?.behaviorType === `$behavior_aggregation`) {
    const {print, t} = i18n;
    return print(
      t(`query-builder.number-of-users-who-did-label`, {
        event: behavior.filters[0].behavior.event.label || behavior.filters[0].behavior.event.value,
      }),
    );
  }
  const renamedProperty = getPropertyDisplayName(propertyName, resourceType, dataGroupId);
  if (renamedProperty !== propertyName) {
    return renamedProperty;
  }

  if (propertyName && propertyName.length > 1 && propertyName.startsWith(`$scd:`)) {
    propertyName = propertyName.replace(`$scd:`, ``);
  }

  if (propertyName === DURATION_PROPERTY_NAME) {
    propertyName = `Duration`;
  }

  // default conversion for all properties starting with '$'
  if (propertyName && propertyName.length > 1 && propertyName.startsWith(`$`)) {
    // Some special case handling here
    if (propertyName in SpecialPropertyMapping) {
      return SpecialPropertyMapping[propertyName as keyof typeof SpecialPropertyMapping];
    }

    return propertyName
      .slice(1)
      .split(`_`)
      .map((str) => capitalize(str))
      .join(` `);
  }
  return propertyName;
}

/*
 * dataGroupId and customProperty are not optional parameters. If the property in question
 * could be a B2B property or custom property and those two parameters are not passed in the
 * property may be improperly renamed.
 */
function renameProperty(
  propertyName: string | string[],
  resourceType: ResourceType,
  _i18n: LocalizationStore,
  {
    dataGroupId = null,
    customProperty = null,
    customPropertyId = null,
    propertyObjectKey = null,
    listItemGroup = null,
    behavior = null,
  }: {
    dataGroupId?: any;
    customProperty?: any;
    customPropertyId?: any;
    propertyObjectKey?: any;
    listItemGroup?: any;
    behavior?: any;
  } = {},
): string {
  const isExperiments = propertyName === MPProperties.Experiments;

  const mainPropertyName =
    _renameProperty(propertyName, resourceType, _i18n, {dataGroupId, customProperty, customPropertyId, behavior}) +
    getPropertyObjectKeySuffix(propertyObjectKey, _i18n, {isExperiments});

  // list of object expressions define some aggregation or nesting within the main property
  // example: Sum of Cart ▸ Price
  if (listItemGroup) {
    return getListItemGroupLabel(listItemGroup, mainPropertyName, _i18n);
  }

  return mainPropertyName;
}

/*
 * dataGroupId and customProperty are not optional parameters. If the property in question
 * could be a B2B property or custom property and those two parameters are not passed in the
 * property may be improperly renamed.
 */
// TODO: renameProperty and getPropertyDisplayName are flawed. It should be checking against
// the right property definition store based on resourceType ('event' or 'user'), which they
// currently cannot do only taking the propertyName as argument
/**
 * @deprecated - use renameProperty instead
 */
export function renamePropertyOld(
  propertyName: string | string[],
  {dataGroupId = null, customProperty = null}: {dataGroupId?: string | null; customProperty?: Obj | null} = {},
): string {
  if (customProperty && customProperty.name) {
    if (Array.isArray(propertyName)) {
      return [customProperty.name, renamePropertyOld(propertyName[1], {dataGroupId})].join(`.`);
    }
    return customProperty.name;
  } else if (Array.isArray(propertyName)) {
    return [renamePropertyOld(propertyName[0]), renamePropertyOld(propertyName[1], {dataGroupId, customProperty})].join(
      `.`,
    );
  }
  const renamedProperty = getPropertyDisplayNameOld(propertyName);
  if (renamedProperty !== propertyName) {
    return renamedProperty;
  }

  // default conversion for all properties starting with '$'
  if (propertyName && propertyName.length > 1 && propertyName.startsWith(`$`)) {
    return propertyName
      .slice(1)
      .split(`_`)
      .map((str) => capitalize(str))
      .join(` `);
  }
  return propertyName;
}

export function getMissingDatagroupMappedProperty(data: any): string {
  return (
    renamePropertyOld(data.join_property_name) +
    `.` +
    renamePropertyOld(data.datagroup_property_name, {dataGroupId: data.data_group_id})
  );
}

export function isMissingDatagroupMappedPropertyLookup(data: any): boolean {
  return data.is_lookup_property;
}

/**
 * {value: '1.5K - 2.5K', prefix: '$'} => '$1.5K - $2.5K'
 */
function formatPropertyValueWithPrefixSuffix(value: string, prefix: string = ``, suffix: string = ``): string {
  const split = value.split(/(\d[\d.,]*[a-zA-Z]?)/).filter(Boolean);
  return split.map((part) => (part.match(/\d/) ? `${prefix}${part}${suffix}` : part)).join(``);
}

export function renamePropertyValue(
  value: string,
  propertyName: string | string[] | undefined,
  groupClause?: GroupClause,
): string {
  const customPropertyId = groupClause?.customPropertyId;
  if (propertyName === `$event_name` || propertyName === `$event`) {
    // Value is an event
    return renameEvent(value);
  } else if (propertyName === `$people`) {
    // Value is a property
    return renamePropertyOld(value); // value is $all_people
  }

  // check against special property values (empty list, undefined, emtpy string, etc...)
  const constantPropertyValue = getOwnAttr(ConstantPropertyValuesInfo, value);
  if (constantPropertyValue) {
    return constantPropertyValue.label;
  }

  // Handle properties with prefix/suffix formatting.
  let propertyDefinition;
  let isNumberProperty = false;
  if (customPropertyId) {
    propertyDefinition = customPropertiesStore.value[customPropertyId];
    isNumberProperty = propertyDefinition?.propertyType === PropertyType.Number;
  } else {
    propertyDefinition =
      !propertyName || propertyName instanceof Array ? null : eventPropertyDefinitionsStore.value[propertyName];
    isNumberProperty = propertyDefinition?.type === PropertyType.Number;
  }
  if (propertyDefinition && isNumberProperty) {
    const formatted = formatPropertyValueWithPrefixSuffix(
      String(value),
      propertyDefinition.displayOptions?.prefix ?? ``,
      propertyDefinition.displayOptions?.suffix ?? ``,
    );
    return formatted;
  }

  const id = parseInt(value, 10);
  const propertyValueMappings = getPropertyValueMappings();
  if (!propertyValueMappings || Number.isNaN(id)) {
    return value;
  }

  const {datasets = {}} = propertyValueMappings;

  if (propertyName === MappedPropertyValues.Dataset) {
    const dataset = datasets[value];
    if (dataset) {
      return `${dataset.name} (id: ${dataset.projectId})`;
    }
  }

  return value;
}
